import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  Button,
  Card,
  Typography,
  Select,
  Input,
  Form,
  Divider,
  Alert,
  Spin,
  Space,
  Tag,
} from "antd";
import {
  DollarOutlined,
  PhoneOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import NumberFormat from "react-number-format";
import toast from "react-hot-toast";
import useBillPayments from "../../hooks/useBillPayments";
import {
  getCustomerIdLabel,
  getCustomerIdPlaceholder,
  getCustomerIdRules,
  getAmountRules,
  calculateFee,
} from "../../utils/billPaymentHelpers";

const { Title, Text } = Typography;
const { Option } = Select;

const BillPaymentsModal = ({
  visible,
  onCancel,
  onPaymentSuccess,
  walletBalance,
}) => {
  const navigate = useNavigate();
  const {
    fetchBillProviders,
    validateBillPayment,
    processBillPayment,
    fetchBillPlans,
    loading: hookLoading,
  } = useBillPayments(navigate);
  const [form] = Form.useForm();
  const [localLoading, setLocalLoading] = useState(false);
  const [providers, setProviders] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plansLoading, setPlansLoading] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [amount, setAmount] = useState(0);
  const [fee, setFee] = useState(0);
  const [validationLoading, setValidationLoading] = useState(false);
  const [validationSuccess, setValidationSuccess] = useState(false);
  const [customerInfo, setCustomerInfo] = useState(null);

  const serviceCategories = [
    {
      key: "airtime",
      label: "Airtime",
      icon: <PhoneOutlined />,
      color: "blue",
    },
    { key: "data", label: "Data", icon: <PhoneOutlined />, color: "purple" },
    {
      key: "electricity",
      label: "Electricity",
      icon: <ThunderboltOutlined />,
      color: "orange",
    },
    {
      key: "television",
      label: "TV/Cable",
      icon: <ThunderboltOutlined />,
      color: "green",
    },
  ];

  useEffect(() => {
    if (visible) {
      fetchProviders();
    }
  }, [visible]);

  const fetchProviders = async () => {
    try {
      const data = await fetchBillProviders();
      const providersData = data?.data || data;

      const groupedProviders = {};
      serviceCategories.forEach((cat) => {
        groupedProviders[cat.key] = [];
      });

      if (providersData && Array.isArray(providersData)) {
        providersData.forEach((item) => {
          const attrs = item.attributes;
          const category = attrs.category?.toLowerCase();

          if (category && groupedProviders[category]) {
            groupedProviders[category].push({
              id: item.id,
              name: attrs.name,
              code: attrs.slug,
              category: category,
              type: "mobile_airtime",
              minAmount: 50,
              maxAmount: 100000,
              billerId: item.id,
              productId: item.id,
            });
          }
        });
      }

      setProviders(groupedProviders);
    } catch (error) {
      console.error("Error fetching providers:", error);
      toast.error("Failed to load bill providers");
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSelectedProvider(null);
    setPlans([]);
    setSelectedPlan(null);
    setCustomerId("");
    setPhoneNumber("");
    setAmount(0);
    form.setFieldsValue({
      provider: undefined,
      plan: undefined,
      customerId: undefined,
      phoneNumber: undefined,
      amount: undefined,
    });
    setFee(0);
  };

  const handleProviderChange = async (providerCode, category) => {
    const provider = providers[category]?.find((p) => p.code === providerCode);
    setSelectedProvider(provider);
    setPlans([]);
    setSelectedPlan(null);
    setCustomerId("");
    setPhoneNumber("");
    setAmount(0);
    form.setFieldsValue({
      plan: undefined,
      amount: undefined,
      customerId: undefined,
      phoneNumber: undefined,
    });
    setFee(0);

    if (provider && ["airtime", "data", "television"].includes(category)) {
      await handleFetchPlans(provider.billerId);
    }
  };

  const handleFetchPlans = async (billerId) => {
    try {
      setPlansLoading(true);
      const plans = await fetchBillPlans(billerId);
      const sortedPlans = [...plans].sort((a, b) => {
        const priceA = a.attributes?.price?.minimumAmount || a.amount || 0;
        const priceB = b.attributes?.price?.minimumAmount || b.amount || 0;
        return priceA - priceB;
      });
      setPlans(sortedPlans);
    } catch (error) {
      console.error("Error fetching plans:", error);
      toast.error("Failed to load available plans");
      setPlans([]);
    } finally {
      setPlansLoading(false);
    }
  };

  const handlePlanChange = (planId) => {
    const plan = plans.find((p) => p.id === planId);
    setSelectedPlan(plan);
    if (plan) {
      setFee(calculateFee(plan.amount));
    }
  };

  const handleCustomAmountChange = (value) => {
    const amount = value || 0;
    if (amount > 0) {
      setFee(calculateFee(amount));
    } else {
      setFee(0);
    }
  };

  const isPlanBased = ["data", "television"].includes(selectedCategory);
  const isAirtimeOrData = ["airtime", "data"].includes(selectedCategory);

  const validateCustomerId = async () => {
    if (!customerId || !selectedProvider) return;

    if (isAirtimeOrData) {
      setValidationSuccess(true);
      return;
    }

    try {
      setValidationLoading(true);
      setValidationSuccess(false);
      const result = await validateBillPayment({
        billerId: selectedProvider.billerId,
        productId: selectedProvider.productId,
        // productSlug: selectedProvider.code,
        category: selectedCategory,
        customerIdentifier: customerId,
      });
      const { data } = result;

      if (data?.isValid || data?.success || data?.valid || data?.customerName) {
        setValidationSuccess(true);
        setCustomerInfo({
          name: data?.customerName,
          number: data?.customerNumber,
          productSlug: data?.productSlug,
        });
        toast.success("Customer validated successfully");
      } else {
        throw new Error(result?.data?.message || "Invalid customer ID");
      }
    } catch (error) {
      console.error("Validation error:", error);
      setValidationSuccess(false);
      setCustomerInfo(null);
      toast.error(
        error.response?.data?.message || error?.message || "Validation failed",
      );
    } finally {
      setValidationLoading(false);
    }
  };

  const handlePayment = async (values) => {
    try {
      setLocalLoading(true);

      if (!selectedProvider) {
        toast.error("Please select a provider");
        return;
      }

      let realAmount, productId;
      if (isPlanBased) {
        if (!selectedPlan) {
          toast.error("Please select a plan");
          return;
        }
        realAmount = selectedPlan.amount;
        productId = selectedPlan.id;
      } else {
        if (!amount) {
          toast.error("Please enter an amount");
          return;
        }
        realAmount = amount;
        productId = selectedProvider.productId;
      }

      const totalAmount = realAmount + fee;
      if (totalAmount > walletBalance) {
        toast.error("Insufficient wallet balance");
        return;
      }

      const paymentPayload = {
        billerId: selectedProvider.billerId,
        productId: productId,
        amount: realAmount,
        customerIdentifier: values.customerId,
        providerName: selectedProvider.code,
        transactionPin: values.pin,
        phoneNumber: phoneNumber,
      };

      if (isPlanBased && selectedPlan?.slug) {
        paymentPayload.planCode = selectedPlan.slug;
      } else if (
        selectedCategory === "electricity" &&
        customerInfo?.productSlug
      ) {
        paymentPayload.planCode = customerInfo.productSlug;
      } else if (selectedCategory === "electricity" && selectedProvider?.code) {
        paymentPayload.planCode = selectedProvider.code;
      }

      if (values.sourceAccountId) {
        paymentPayload.sourceAccountId = values.sourceAccountId;
      }

      const result = await processBillPayment(paymentPayload);

      toast.success(result?.message || "Payment successful!");
      onPaymentSuccess?.();
      handleCancel();
    } catch (error) {
      console.log("Error processing payment:", error);
      toast.error(error.response?.data?.message || "Payment failed");
    } finally {
      setLocalLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedCategory(null);
    setSelectedProvider(null);
    setPlans([]);
    setSelectedPlan(null);
    setCustomerId("");
    setPhoneNumber("");
    setAmount(0);
    setFee(0);
    setValidationSuccess(false);
    setCustomerInfo(null);
    onCancel();
  };

  return (
    <Modal
      title={
        <div className="text-center">
          <Title level={3} className="mb-2">
            KONNECT BILL PORTAL
          </Title>
          <Text className="text-gray-600">Pay bills instantly</Text>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      centered
      style={{ maxHeight: "90vh" }}
      bodyStyle={{ maxHeight: "calc(90vh - 120px)", overflowY: "auto" }}
    >
      <div className="py-4">
        {/* Wallet Balance */}
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarOutlined className="text-blue-500" />
              <Text strong>Wallet Balance</Text>
            </div>
            <Text strong>
              ₦
              <NumberFormat
                thousandSeparator={true}
                displayType="text"
                value={Math.floor(walletBalance).toFixed(2)}
              />
            </Text>
          </div>
        </div>

        <Form form={form} layout="vertical" onFinish={handlePayment}>
          {/* Service Category */}
          <Form.Item
            label="Service Category"
            name="category"
            rules={[{ required: true, message: "Please select a category" }]}
          >
            <Select
              placeholder="Select service category"
              onChange={handleCategoryChange}
            >
              {serviceCategories.map((cat) => (
                <Option key={cat.key} value={cat.key}>
                  <Space>
                    {cat.icon}
                    {cat.label}
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>
          {/* Provider */}
          {selectedCategory && providers[selectedCategory]?.length > 0 && (
            <Form.Item
              label="Provider"
              name="provider"
              rules={[{ required: true, message: "Please select a provider" }]}
            >
              <Select
                placeholder="Select provider"
                loading={plansLoading}
                onChange={(value) =>
                  handleProviderChange(value, selectedCategory)
                }
              >
                {providers[selectedCategory].map((p, index) => (
                  <Option
                    key={`${selectedCategory}-${p.code}-${index}`}
                    value={p.code}
                  >
                    {p.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
          {/* Plan Selection */}
          {isPlanBased && selectedProvider && (
            <Form.Item
              label="Plan"
              name="plan"
              rules={[{ required: true, message: "Please select a plan" }]}
            >
              <Spin spinning={plansLoading}>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                    <p className="font-semibold text-gray-900">
                      Available Plans
                    </p>
                    {plans.length > 0 && (
                      <span className="text-sm text-gray-600">
                        {plans.length} plans
                      </span>
                    )}
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    <div className="space-y-2 p-3">
                      {plans.length === 0 ? (
                        <div className="text-center py-6">
                          <p className="text-gray-500">No plans available</p>
                        </div>
                      ) : (
                        plans.map((p) => (
                          <div
                            key={p.id}
                            onClick={() => {
                              handlePlanChange(p.id);
                              form.setFieldValue("plan", p.id);
                            }}
                            className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                              form.getFieldValue("plan") === p.id
                                ? "border-black bg-gray-50"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            <div className="flex justify-between items-start gap-3">
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-900 text-sm">
                                  {p.name}
                                </p>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <p className="font-bold text-base text-black whitespace-nowrap">
                                  ₦
                                  <NumberFormat
                                    thousandSeparator={true}
                                    displayType="text"
                                    value={p.amount}
                                  />
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </Spin>
            </Form.Item>
          )}
          {(selectedCategory === "airtime" ||
            selectedCategory === "electricity") &&
            selectedProvider && (
              <Form.Item label="Amount" name="amount" rules={getAmountRules()}>
                <NumberFormat
                  placeholder={`Enter amount (₦10 - ₦1,000,000)`}
                  thousandSeparator={true}
                  prefix="₦"
                  value={amount}
                  onValueChange={(values) => {
                    const numValue = values.floatValue || 0;
                    setAmount(numValue);
                    form.setFieldValue("amount", numValue);
                    if (numValue > 0) {
                      handleCustomAmountChange(numValue);
                    } else {
                      setFee(0);
                    }
                  }}
                  customInput={Input}
                  allowNegative={false}
                  decimalScale={2}
                  fixedDecimalScale={true}
                />
              </Form.Item>
            )}
          {/* Customer ID */}
          {selectedProvider && (
            <Form.Item
              label={getCustomerIdLabel(selectedCategory)}
              name="customerId"
              rules={getCustomerIdRules(selectedCategory)}
            >
              <Input
                placeholder={getCustomerIdPlaceholder(selectedCategory)}
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                onBlur={validateCustomerId}
                suffix={
                  validationLoading ? (
                    <Spin size="small" />
                  ) : validationSuccess ? (
                    <CheckCircleOutlined className="text-green-500" />
                  ) : null
                }
              />
            </Form.Item>
          )}
          {/* Customer Info Display */}
          {customerInfo && (
            <Alert
              message=""
              description={
                <p>
                  <strong>Customer Verified:</strong> {customerInfo.name}
                </p>
              }
              type="success"
              showIcon
              className="mb-4"
            />
          )}
          {/* Phone Number (Electricity only) */}
          {selectedCategory === "electricity" && selectedProvider && (
            <Form.Item
              label="Phone Number"
              name="phoneNumber"
              rules={[
                { required: true, message: "Please enter phone number" },
                {
                  pattern: /^(\+234|0)[789]\d{9}$/,
                  message: "Please enter a valid Nigerian phone number",
                },
              ]}
            >
              <Input
                placeholder="Enter phone number (e.g., 08012345678)"
                value={phoneNumber}
                type="number"
                inputMode="numeric"
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
            </Form.Item>
          )}
          {/* Fee Display */}
          {fee > 0 && (
            <Card className="mb-4 bg-gray-50">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Text>Amount:</Text>
                  <Text>
                    ₦
                    <NumberFormat
                      thousandSeparator={true}
                      displayType="text"
                      value={
                        isPlanBased ? selectedPlan?.amount || 0 : amount || 0
                      }
                    />
                  </Text>
                </div>
                {/* <div className='flex justify-between'>
                  <Text>Fee:</Text>
                  <Text>
                    ₦
                    <NumberFormat
                      thousandSeparator={true}
                      displayType='text'
                      value={fee}
                    />
                  </Text>
                </div> */}
                <Divider className="my-2" />
                <div className="flex justify-between font-semibold">
                  <Text>Total:</Text>
                  <Text>
                    ₦
                    <NumberFormat
                      thousandSeparator={true}
                      displayType="text"
                      value={
                        isPlanBased ? selectedPlan?.amount || 0 : amount || 0
                      }
                    />
                  </Text>
                </div>
              </div>
            </Card>
          )}
          {/* PIN */}
          <Form.Item
            label="Transaction PIN"
            name="pin"
            rules={[{ required: true, message: "Please enter your PIN" }]}
          >
            <Input.Password
              inputMode="numeric"
              placeholder="Enter 4-digit PIN"
              maxLength={4}
            />
          </Form.Item>{" "}
          <button
            className="font-gilroyBold text-blue-500"
            onClick={() => {
              navigate(`/profile/trans-pin?pin=${true}`);
            }}
          >
            Create or update your transaction pin here
          </button>
          <Form.Item className="mt-6">
            <Space className="w-full" direction="vertical" size="middle">
              <Button
                type="primary"
                htmlType="submit"
                block
                size="large"
                loading={localLoading || hookLoading}
                disabled={
                  isPlanBased
                    ? !selectedPlan || !customerId
                    : !amount || !customerId
                }
              >
                Pay Bill
              </Button>

              <Button block onClick={handleCancel}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default BillPaymentsModal;
